import { useEffect, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useProfile } from '@/api/profile'
import { api } from '@/lib/api'
import { runStaggered } from '@/lib/idleTask'

const FIVE_MIN = 5 * 60_000
const ONE_HOUR = 60 * 60_000

/**
 * Prefetch des queries utiles aux 2 espaces (Manager + Agent softphone).
 *
 * Démarre seulement après que `useProfile()` retourne data (= user logué).
 * Fire-and-forget en idle pour ne pas concurrencer la page principale.
 *
 * Aucun blocage : si une query échoue, le hook normal du composant qui en
 * a besoin refera son fetch quand l'user navigue. Le prefetch est un bonus
 * de performance, pas une dépendance critique.
 */
export function AuthBootProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const { data: profile } = useProfile()

  useEffect(() => {
    if (!profile) return

    // Cascade : 2 tâches au total — on les lance ensemble (1 seul lot).
    // Délai initial 2s pour laisser l'user interagir avec la page courante.
    return runStaggered([
      () => {
        queryClient.prefetchQuery({
          queryKey: ['voice-choices'],
          queryFn: async () => {
            const { data } = await api.get<{
              choices: { value: string; label: string }[]
            }>('/api/voice-choices/')
            return data.choices ?? []
          },
          staleTime: ONE_HOUR,
        })
      },
      () => {
        queryClient.prefetchQuery({
          queryKey: ['subscription-status'],
          queryFn: async () => {
            const { data } = await api.get('/api/stripe/subscription-status/')
            return data
          },
          staleTime: FIVE_MIN,
        })
      },
    ])
  }, [profile, queryClient])

  return <>{children}</>
}
